'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose
} from '@/components/Dialog';
import { Button } from '@/components/Button/ButtonV2';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/SelectV3';
import { AlertCircle, Calendar, Sparkles, Clock, X, Plus, Trash2 } from 'lucide-react';
import { generateTimeOptions, timeToMinutes } from '../utils';
import { days, quickOptions } from '../constants';
import { DayKey, QuickOption } from '@/containers/CustomerJourneySetup/context/types';
import { Alert } from '@/components/Alert/Alert';
import { trackGenericEvent } from '@/analytics';

interface DialogQuickAddProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAddTimeSlot: (days: DayKey[], start: string, end: string) => void;
  onAddMultipleTimeSlots?: (days: DayKey[], ranges: TimeRange[]) => void;
}

interface TimeRange {
  start: string;
  end: string;
}

export const DialogQuickAdd = ({
  isOpen,
  onOpenChange,
  onAddTimeSlot,
  onAddMultipleTimeSlots
}: DialogQuickAddProps) => {
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>([{ start: '09:00', end: '18:00' }]);
  const [selectedDays, setSelectedDays] = useState<DayKey[]>([]);
  const [activeQuickOption, setActiveQuickOption] = useState<string | null>(null);
  const timeOptions = generateTimeOptions();

  // Validation for all time ranges
  const hasTimeErrors = timeRanges.some((range) => timeToMinutes(range.end) <= timeToMinutes(range.start));
  const hasOverlapErrors = () => {
    for (let i = 0; i < timeRanges.length; i++) {
      for (let j = i + 1; j < timeRanges.length; j++) {
        const range1 = timeRanges[i];
        const range2 = timeRanges[j];

        const start1 = timeToMinutes(range1.start);
        const end1 = timeToMinutes(range1.end);
        const start2 = timeToMinutes(range2.start);
        const end2 = timeToMinutes(range2.end);

        if ((start1 <= start2 && start2 < end1) || (start2 <= start1 && start1 < end2)) {
          return true;
        }
      }
    }
    return false;
  };

  const hasDaysError = selectedDays.length === 0;
  const hasAnyErrors = hasTimeErrors || hasOverlapErrors() || hasDaysError;

  // Reset the active quick option when days are manually changed
  useEffect(() => {
    const matchedOption = quickOptions.find(
      (option) =>
        selectedDays.length === option.days.length &&
        option.days.every((d) => selectedDays.includes(d as DayKey))
    );

    setActiveQuickOption(matchedOption?.id || null);
  }, [selectedDays]);

  const handleSelectQuickOption = (option: QuickOption) => {
    setSelectedDays(option.days as DayKey[]);
    setActiveQuickOption(option.id);

    // Track quick option selection
    trackGenericEvent('Journey Working Hours Quick Option Selected', {
      optionId: option.id,
      optionLabel: option.label,
      daysCount: option.days.length
    });
  };

  const handleToggleDay = (day: DayKey) => {
    const wasSelected = selectedDays.includes(day);
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

    // Track day toggle
    trackGenericEvent('Journey Working Hours Quick Add Day Toggled', {
      day,
      selected: !wasSelected,
      totalSelected: wasSelected ? selectedDays.length - 1 : selectedDays.length + 1
    });
  };

  const handleUpdateTimeRange = (index: number, field: 'start' | 'end', value: string) => {
    setTimeRanges((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddTimeRange = () => {
    if (timeRanges.length === 0) {
      setTimeRanges([{ start: '09:00', end: '18:00' }]);
      return;
    }

    const lastRange = timeRanges[timeRanges.length - 1];
    const lastEndMinutes = timeToMinutes(lastRange.end);

    // Find the next available time slot after the last range
    const nextStartOption = timeOptions.find((time) => timeToMinutes(time) > lastEndMinutes);

    const newStartTime = nextStartOption || lastRange.end;

    // Find a reasonable end time (at least 1 hour after start, or last available time)
    const newStartMinutes = timeToMinutes(newStartTime);
    const defaultEndOption = timeOptions.find((time) => timeToMinutes(time) >= newStartMinutes + 60); // 1 hour later

    const newEndTime = defaultEndOption || timeOptions[timeOptions.length - 1];

    setTimeRanges((prev) => [...prev, { start: newStartTime, end: newEndTime }]);
  };

  const handleDeleteTimeRange = (index: number) => {
    if (timeRanges.length > 1) {
      setTimeRanges((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleApply = () => {
    // Use the multiple time slots function if available, otherwise fall back to individual calls
    if (onAddMultipleTimeSlots && timeRanges.length > 1) {
      onAddMultipleTimeSlots(selectedDays, timeRanges);
    } else {
      // Apply each time range to the selected days
      timeRanges.forEach((range) => {
        onAddTimeSlot(selectedDays, range.start, range.end);
      });
    }
    onOpenChange(false);

    // Track successful quick add
    trackGenericEvent('Journey Working Hours Quick Add Applied', {
      daysCount: selectedDays.length,
      timeRangesCount: timeRanges.length,
      selectedDays,
      timeRanges
    });
  };

  const resetSelection = () => {
    setSelectedDays([]);
    setActiveQuickOption(null);
    setTimeRanges([{ start: '09:00', end: '18:00' }]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetSelection();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-xl shadow-lg max-h-[90vh] sm:max-h-[90vh] flex flex-col">
        <DialogHeader className="border-b pb-4 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Calendar className="h-6 w-6 text-primary" />
            Agregar horarios
          </DialogTitle>
          <DialogClose />
        </DialogHeader>

        <div className="space-y-4 py-4 px-1 overflow-y-auto flex-1 max-h-[calc(90vh-140px)]">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {quickOptions.map((option) => (
                <Button
                  key={option.id}
                  variant={activeQuickOption === option.id ? 'default' : 'outline'}
                  size="sm"
                  className={`
                    rounded-lg transition-all duration-200 w-full
                    ${
                      activeQuickOption === option.id
                        ? 'shadow-sm border-blue bg-blue text-white'
                        : 'border-gray-200 hover:border-blue/50 hover:bg-blue/5'
                    }
                  `}
                  onClick={() => handleSelectQuickOption(option)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium text-gray-700">Selecciona los días</label>
              {selectedDays.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs leading-none text-red-500 hover:text-red-600 flex items-center gap-1 hover:bg-red-50"
                  onClick={resetSelection}
                >
                  <div className="flex items-center gap-1">
                    <X className="h-4 w-4" />
                    <span>Limpiar selección</span>
                  </div>
                </Button>
              )}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {days.map((day) => {
                const isSelected = selectedDays.includes(day.key);
                return (
                  <button
                    key={day.key as string}
                    className={`
                    flex flex-col border items-center justify-center px-2 py-2 rounded-md transition-all duration-200
                    ${
                      isSelected
                        ? 'shadow-sm border-blue bg-blue text-white'
                        : 'text-blue border-gray-200 hover:border-blue/50 hover:bg-blue/5'
                    }
                  `}
                    onClick={() => handleToggleDay(day.key)}
                  >
                    <span className="text-sm font-medium">{day.shortLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedDays.length > 0 && (
            <div className="space-y-3">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-4">
                {timeRanges.map((range, index) => {
                  const previousRange = timeRanges[index - 1];
                  const nextRange = timeRanges[index + 1];
                  const minTime = previousRange ? previousRange.end : undefined;
                  const maxTime = nextRange ? nextRange.start : undefined;
                  const isInvalid = timeToMinutes(range.end) <= timeToMinutes(range.start);

                  return (
                    <div key={index} className="space-y-2">
                      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                        <div className="space-y-2">
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Hora de inicio
                          </label>
                          <Select
                            value={range.start}
                            onValueChange={(value) => handleUpdateTimeRange(index, 'start', value)}
                          >
                            <SelectTrigger className="border-gray-200 bg-white">
                              <SelectValue placeholder="Selecciona hora" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions
                                .filter((time) => {
                                  const timeMinutes = timeToMinutes(time);
                                  const minTimeMinutes = minTime ? timeToMinutes(minTime) : 0;
                                  const maxTimeMinutes = maxTime ? timeToMinutes(maxTime) : 1440;

                                  // For start time, we should allow any time that fits between adjacent ranges
                                  // Don't restrict by current end time as that prevents editing
                                  return timeMinutes >= minTimeMinutes && timeMinutes < maxTimeMinutes;
                                })
                                .map((time) => (
                                  <SelectItem key={time} value={time}>
                                    {time}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Hora de fin
                          </label>
                          <Select
                            value={range.end}
                            onValueChange={(value) => handleUpdateTimeRange(index, 'end', value)}
                          >
                            <SelectTrigger
                              className={`border-gray-200 bg-white ${
                                isInvalid ? 'border-red-300 ring-1 ring-red-300' : ''
                              }`}
                            >
                              <SelectValue placeholder="Selecciona hora" />
                            </SelectTrigger>
                            <SelectContent>
                              {timeOptions
                                .filter((time) => {
                                  const timeMinutes = timeToMinutes(time);
                                  const startTimeMinutes = timeToMinutes(range.start);
                                  const maxTimeMinutes = maxTime ? timeToMinutes(maxTime) : 1440; // 24:00
                                  return timeMinutes > startTimeMinutes && timeMinutes <= maxTimeMinutes;
                                })
                                .map((time) => (
                                  <SelectItem key={time} value={time}>
                                    {time}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {timeRanges.length > 1 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTimeRange(index)}
                            className="text-red-500 hover:bg-red-50 border-red-200 border mt-6"
                            aria-label="Eliminar rango"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center justify-center">
                        {isInvalid && (
                          <span className="text-xs text-red-500 bg-red-50 px-3 py-1 rounded-full border border-red-200 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Horario inválido
                          </span>
                        )}
                      </div>
                      {index < timeRanges.length - 1 && <div className="border-b border-gray-200"></div>}
                    </div>
                  );
                })}
                {timeRanges.length < 5 && (
                  <div className="flex justify-center pt-2">
                    <Button variant="outline" size="sm" onClick={handleAddTimeRange}>
                      <div className="flex items-center gap-2">
                        <Plus className="h-3 w-3" />
                        Agregar rango
                      </div>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasDaysError && (
            <Alert
              variant="destructive"
              className="flex items-center gap-2 bg-red-50 text-red-500 p-3 rounded-lg border border-red-200"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Debes seleccionar al menos un día</span>
            </Alert>
          )}

          {hasTimeErrors && (
            <Alert
              variant="destructive"
              className="flex items-center gap-2 bg-red-50 text-red-500 p-3 rounded-lg border border-red-200"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Uno o más rangos tienen horas inválidas</span>
            </Alert>
          )}

          {hasOverlapErrors() && (
            <Alert
              variant="destructive"
              className="flex items-center gap-2 bg-red-50 text-red-500 p-3 rounded-lg border border-red-200"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Los rangos horarios no pueden superponerse</span>
            </Alert>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2 border-t flex-shrink-0">
          <Button
            variant="outline"
            className="w-full sm:w-auto border-gray-200 text-gray-700 hover:bg-gray-50"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            className="w-full sm:w-auto bg-primary hover:bg-primary/90 transition-colors"
            onClick={handleApply}
            disabled={hasAnyErrors}
          >
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span>Aplicar horarios</span>
            </div>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
